/**
 * 65.30 (decision 179, the owner: *"relate our classes of ammo conceptually, and slot down specifically what 'ammo type' is
 * being used for the current weapons"*): the cartridges. A round holds its damage, its muzzle speed (units/s at classic
 * scale; the scale profile multiplies it by k and the bullets' speed tuning, core/weapons.ts) and its life (s); a weapon
 * (GROUND_WEAPONS, MOUNTED_GUNS) keeps its rate, spread, magazine and reload and names its round. The rocket is no
 * cartridge (its own numbers, constants/action.ts). Part of src/constants.ts (re-exported there; import from '../constants').
 */
import type { CartridgeKind } from '../types';

/** The rifle's round, the base every other is a ratio of: muzzle speed (units/s), damage, life (s). */
export const BULLET_SPEED = 400;
export const BULLET_DAMAGE = 20;
export const BULLET_LIFE = 1.5;

/**
 * A round: `damage` at AMMO_REFERENCE_PEOPLE (more at the muzzle, less beyond: the drag, through the speed-share rule),
 * muzzle `speed`, `life`, and `halfSpeedPeople` — the distance over which the drag halves its speed (quadratic drag,
 * core/ballistics.ts: v(x) = v₀·e^(−c·x), c = ln 2 / that distance).
 */
export interface Cartridge { damage: number; speed: number; life: number; halfSpeedPeople: number }

/** 65.30 (the coordinator's ruling (a)): a round's damage is quoted where the fights are — today's numbers hold at 30 people. */
export const AMMO_REFERENCE_PEOPLE = 30;

/**
 * 9 mm (the pistol and the SMG, 65.13b: one round), 7.62 (the rifle and the LMG — the owner's ruling), .50 (the HMG) and
 * 5.56 (for the later rifle: less damage, faster and farther — no weapon fires it yet). Today's speeds and lives; the
 * damage today's at 30 people but the .50's, made lethal (decision 179: a two-hit kill, out to ~290 people).
 */
export const CARTRIDGES: Record<CartridgeKind, Cartridge> = {
  '9mm': { damage: BULLET_DAMAGE * 0.75, speed: BULLET_SPEED * 0.7, life: BULLET_LIFE * 0.6, halfSpeedPeople: 90 },
  '7.62': { damage: BULLET_DAMAGE, speed: BULLET_SPEED, life: BULLET_LIFE, halfSpeedPeople: 350 },
  '.50': { damage: BULLET_DAMAGE * 3, speed: BULLET_SPEED * 1.35, life: BULLET_LIFE * 1.6, halfSpeedPeople: 1000 },
  '5.56': { damage: BULLET_DAMAGE * 0.8, speed: BULLET_SPEED * 1.2, life: BULLET_LIFE, halfSpeedPeople: 500 },
  /** 65.43 (the owner: "Classic gun numbers look pretty reasonable"): the .30-06 — a little more than the 7.62, everywhere. */
  '.30-06': { damage: BULLET_DAMAGE * 1.1, speed: BULLET_SPEED * 1.02, life: BULLET_LIFE, halfSpeedPeople: 380 },
  /** 65.43: the .45 ACP — heavier and slower than the 9 mm (real 250 m/s to its 360, through the same compression): hits harder up close, drops sooner. */
  '.45': { damage: BULLET_DAMAGE * 0.75 * 1.2, speed: BULLET_SPEED * 0.6, life: BULLET_LIFE * 0.6, halfSpeedPeople: 70 },
};
